/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Dev harness for the ORTB body editor (#858) — a public, unauthenticated route
 * (`/dev/ortb-body-editor`) that renders {@link OrtbBodyEditor} in isolation with
 * the pilot BidRequest pre-loaded, alongside a live view of the emitted
 * {@link OrtbBodyTemplate}. Lets us eyeball the real component (Chakra + live
 * tokens, dark mode) without navigating the full wizard/auth flow.
 *
 * Not part of the product IA — a development/design surface only.
 */

import React from 'react';
import { Box, Button, Container, HStack, Heading, Text, VStack } from '@chakra-ui/react';
import { OrtbBodyEditor } from '../components/shared/editors/OrtbBodyEditor';
import type { OrtbBodyTemplate } from '../types/demand-source';

const PILOT_TEMPLATE: OrtbBodyTemplate = {
  mode: 'merge',
  body: {
    device: { w: '{w}', h: '{h}', ua: '{ua}', ifa: '{idfa}', dnt: 0 },
    regs: { ext: { us_privacy: '{us_privacy}', gdpr: 0 }, coppa: 0 },
    ext: {
      number_of_ads: 6,
      kvps: { pub_genre: '{genre}', pod_placement: 'preroll', unique_pod_id: '{unique_pod_id}' },
    },
    imp: [
      {
        id: '1',
        tagid: '28125',
        bidfloor: { $expr: { type: 'calculated', path: 'pod.roundFloorCpm', default: '1.25' }, $valueType: 'number' },
      },
    ],
  },
};

export const OrtbBodyEditorDevPage: React.FC = () => {
  const [template, setTemplate] = React.useState<OrtbBodyTemplate>(PILOT_TEMPLATE);
  const [dark, setDark] = React.useState(false);

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  return (
    <Box minH="100vh" bg="bg.canvas" py={8}>
      <Container maxW="6xl">
        <HStack mb={6} justify="space-between" flexWrap="wrap" gap={3}>
          <VStack align="start" gap={1}>
            <Heading size="lg" color="fg.heading">
              ORTB Body Editor — dev harness
            </Heading>
            <Text color="fg.muted" fontSize="sm">
              #858 · guided leaf-mapping + paste-to-map. Not part of the product nav.
            </Text>
          </VStack>
          <HStack gap={2}>
            <Button size="sm" variant="outline" borderColor="border.default" onClick={() => setDark((d) => !d)}>
              {dark ? 'Light' : 'Dark'} mode
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor="border.default"
              onClick={() => setTemplate(PILOT_TEMPLATE)}
            >
              Reset to pilot
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor="border.default"
              onClick={() => setTemplate({ mode: 'merge', body: {} })}
            >
              Clear
            </Button>
          </HStack>
        </HStack>

        <Box display="grid" gridTemplateColumns={{ base: '1fr', lg: '1.4fr 1fr' }} gap={6} alignItems="start">
          <OrtbBodyEditor
            /* A standalone template, with no parent (`#1379`). */
            parentRemovals={null}
            rememberKey={null}
            fromTemplate={null}
            requestOccurrences={null}
            floorBound={null}
            /* `null` — the dev harness has no validation panel and nothing to point with. */
            openRow={null}
            // Standalone dev harness: no demand source, so nothing to resolve against.
            // The one legitimate `null` of the three (see `e928571f`).
            preview={null}
            /* A dev harness edits no artifact, so it genuinely does not know — which is what
           `null` is for, and why this prop is nullable rather than optional. */
            isTemplate={null}
            /* No source, so no declared version: `null` is the default (2.6). */
            ortbVersion={null}
            onSaveExpressionToLibrary={null}
            value={template}
            onChange={setTemplate}
            onQuickTest={() => window.alert('Quick test — wired to buildOpenRtbQuickTestPayload in the wizard.')}
          />

          <Box borderWidth="1px" borderColor="border.default" borderRadius="lg" bg="bg.card" overflow="hidden">
            <Text
              px={4}
              py={3}
              borderBottomWidth="1px"
              borderColor="border.default"
              fontFamily="mono"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="wide"
              color="fg.muted"
            >
              Emitted OrtbBodyTemplate (what saves + #862 resolves)
            </Text>
            <Box as="pre" p={4} overflowX="auto" fontFamily="mono" fontSize="xs" lineHeight="1.7" color="fg.default">
              {JSON.stringify(template, null, 2)}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
